import type { BigIdea, StandardContent } from '@innovamat/ga-features';

import type { Printable } from '../types/Materials';
import type {
  PreprModel,
  SmartGuideBase,
  SmartGuidePrimaria,
  SmartGuideSecundaria,
  SmartGuideTaller,
  SmartGuideType,
} from '../types/smart-guide';
import type { Stage } from '../types/Stage';
import { mapBigIdeas } from './map-big-ideas';
import { mapDimensionTags } from './map-dimension-tags';
import { mapMaterials } from './map-materials';
import { mapNewMathPractices } from './map-new-math-practices';
import { mapNewStandardContent } from './map-new-standard-content';
import { groupParts } from './map-part';

type MapSmartGuideParams = {
  data: any;
  stage: Stage;
  model: PreprModel;
  printables: Printable[];
  regionParameters: any;
  isCaliforniaStandard: boolean;
  isBubbleTreeStandard: boolean;
  assessables?: string[];
  standardContent?: StandardContent[];
  mathPractices?: string[];
  bigIdeas?: BigIdea[];
};

const modelToKey = (model: string) => {
  const modelKey = {
    SmartGuideTaller: 'ey',
    SmartGuidePrimaria: 'pe',
    SmartGuideSecundaria: 'se',
    SmartGuideOpen: 'se',
  };

  return modelKey[model as keyof typeof modelKey];
};

const guideTypeMap = (tipo: string) => {
  const guideType = {
    1: 'lab',
    2: 'adv',
    3: 'us',
    4: '4in1',
  };

  return guideType[+tipo as keyof typeof guideType];
};

export function mapSmartGuide({
  data,
  stage,
  model,
  printables,
  regionParameters,
  isCaliforniaStandard,
  assessables,
  standardContent,
  mathPractices,
  bigIdeas,
  isBubbleTreeStandard,
}: MapSmartGuideParams): SmartGuideType {
  const smartGuide: SmartGuideBase = {
    type: 'smartGuide',
    id: data._id,
    sessionCode: data.session_code,
    summary: {
      inThisTaller: {
        titleKey:
          modelToKey(model) === 'ey'
            ? `digitalguides.${modelToKey(model)}.workshop.summary`
            : `digitalguides.${modelToKey(model)}.summary`,
        text: data.en_este_taller,
      },
      hasAdditionalTraining: {
        titleKey: `smartguides.additional_training`,
        value: data.additional_training,
      },
      withIntentionOf: {
        titleKey: `digitalguides.${modelToKey(model)}.with_intention_of`,
        text: data.con_intencin_de,
      },
      vocabularyKey: {
        titleKey: 'digitalguides.key_vocabulary',
        text: data.vocabulario_clave,
      },

      assessables: assessables || [],
      materials: mapMaterials(data.materiales, printables, stage),
      dimensionTags: mapDimensionTags(data.dimensiones, isBubbleTreeStandard),
      newStandardContent: mapNewStandardContent({
        data,
        stage,
        standardContent,
        isCaliforniaStandard,
        isBubbleTreeStandard,
      }),
      newMathPractices: mapNewMathPractices(mathPractices),
      bigIdeas: mapBigIdeas(bigIdeas),
    },
    subtitle: data.subtitle,
    title: data.title,
    parts: groupParts({
      data,
      modelKey: modelToKey(model),
      guideType: guideTypeMap(data.tipo),
      regionParameters,
      isCaliforniaStandard,
      locale: data._locale,
    }),
  };

  if (model === 'SmartGuidePrimaria') {
    const processedSmartGuide: SmartGuidePrimaria = {
      __typename: 'SmartGuidePrimaria',
      ...smartGuide,
      guideType: data.tipo,
    };
    return processedSmartGuide;
  }

  if (model === 'SmartGuideSecundaria' || model === 'SmartGuideOpen') {
    const processedSmartGuide: SmartGuideSecundaria = {
      __typename: 'SmartGuideSecundaria',
      ...smartGuide,
      summary: {
        ...smartGuide.summary,
        vocabularyKey: {
          titleKey: 'digitalguides.key_vocabulary',
          text: data.vocabulario_sec,
        },
        toConsider: {
          titleKey: 'digitalguides.to_consider',
          text: data.vocabulario_clave,
        },
      },
    };

    return processedSmartGuide;
  }

  const processedSmartGuide: SmartGuideTaller = {
    __typename: 'SmartGuideTaller',
    ...smartGuide,
  };

  return processedSmartGuide;
}
